<template>
  <div
    :class="{
      'editable-content--full-width': buttonPosition === 'down',
      'with-hidden-button': triggerHidden,
      'buttons-on-left': buttonsOnLeft
    }"
    class="editable-content"
    @mouseenter="showTrigger()"
    @mouseleave="hideTrigger()"
  >
    <div v-if="buttonsOnLeft" class="editable-content__action col-auto">
      <q-btn
        ref="trigger"
        :round="buttonRound"
        :outline="buttonPosition === 'down' || !buttonFlat"
        :flat="buttonPosition !== 'down' || buttonFlat"
        :color="buttonPosition === 'down' ? 'tertiary' : ''"
        :class="{
          hidden: triggerOnHover && triggerHidden,
          visible: !triggerHidden || !triggerOnHover
        }"
        size="sm"
        class="q-mr-xs"
        @click="toggleEditing()"
      >
        <i class="fas fa-pencil-alt" />
      </q-btn>
    </div>
    <div v-if="!hideContentSlot" ref="content" class="editable-content__text col-auto">
      <div class="content trim">
        <div class="slot">
          <div class="content-inner">
            <slot name="content" />
          </div>
        </div>
      </div>
    </div>
    <div v-if="!buttonsOnLeft" class="editable-content__action col-auto">
      <q-btn
        ref="trigger"
        :round="buttonRound"
        :outline="buttonPosition === 'down' || !buttonFlat"
        :flat="buttonPosition !== 'down' || buttonFlat"
        :color="buttonPosition === 'down' ? 'tertiary' : ''"
        :class="{
          hidden: triggerOnHover && triggerHidden,
          visible: !triggerHidden || !triggerOnHover
        }"
        size="sm"
        class="q-ml-xs hoverable-btn"
        @click="toggleEditing()"
      >
        <i class="fas fa-pencil-alt" />
      </q-btn>
    </div>
    <div
      v-if="editing"
      ref="editorPopover"
      :style="style"
      :class="{
        'editable-field-container--left': position === 'left',
        'editable-field-container--left-end': position === 'left-end',
        'editable-field-container--right': position === 'right',
        'editable-field-container--small': size === 'small',
        'editable-field-container--big': size === 'big',
        'editable-field-container--button-down': buttonPosition === 'down',
        'positioned-high': positionedHigh
      }"
      class="editable-field-container"
    >
      <div class="editable-field-container__header">
        <h3 class="editable-field-container__title">Update {{ title }}</h3>
      </div>
      <form class="editable-field-container__content q-field-content" style="width: 100%" @submit.prevent="save">
        <div class="row no-wrap q-pa-sm">
          <template v-if="type === 'select'">
            <enhanced-q-select
              id="select"
              slot="field"
              ref="select"
              v-model="newValue"
              :options="options"
              class="col"
              dense
            />
          </template>
          <template v-if="type === 'text'">
            <q-input ref="text-input" slot="field" v-model="newValue" type="text" outlined dense />
          </template>
          <template v-if="type === 'advanced'">
            <q-editor
              slot="field"
              ref="advancedEditor"
              v-model="newValue"
              :content-style="{ listStylePosition: 'inside' }"
              :fonts="fonts"
              :toolbar="toolbarSettings"
              toolbar-text-color="grey"
              toolbar-toggle-color="tertiary"
              max-height="500px"
              toolbar-flat
              @input="strip"
            />
          </template>
          <template v-if="type === 'date'">
            <q-date v-model="newValue" :options="datePickerOptions()" minimal />
          </template>

          <div class="actions">
            <q-btn color="tertiary" outline type="submit">
              <i class="fas fa-save" />
            </q-btn>

            <q-btn flat text-color="black" @click="toggleEditing">
              <i class="fas fa-times" />
            </q-btn>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { stripHtmlFromInput } from '@/lib/formatting.utils'
import Q_EDITOR_MIXIN from '@/mixins/q-editor-mixin'
import { placeCaretAtEnd } from '@/lib/generic.utils'
import moment from 'moment'
// Components
import EnhancedQSelect from '@/components/general/EnhancedQSelect'

export default {
  name: 'EntityEditor',
  components: {
    EnhancedQSelect
  },
  mixins: [Q_EDITOR_MIXIN],
  props: {
    params: {
      type: Object,
      default: () => {
        return {}
      }
    },
    type: {
      type: String,
      required: true
    },
    // Use this prop to pass a date for computing min prop on datetime inputs
    dependency: {
      type: [String, null],
      default: null
    },
    maxDate: [Date, String],
    title: {
      type: String,
      required: true
    },
    position: {
      type: String,
      default: 'right'
    },
    size: {
      type: String,
      default: 'medium'
    },
    buttonPosition: {
      type: String,
      default: 'right'
    },
    options: {
      type: Array,
      default: () => []
    },
    containerStyle: {
      type: Object,
      default: () => {
        return {}
      }
    },
    modelFrom: {
      type: [Object, Array, String, Date],
      default: ''
    },
    hideContentSlot: {
      type: Boolean,
      default: false
    },
    buttonsOnLeft: {
      type: Boolean,
      default: false
    },
    positionedHigh: {
      type: Boolean,
      default: false
    },
    triggerOnHover: {
      type: Boolean,
      default: true
    },
    buttonFlat: {
      type: Boolean,
      default: false
    },
    buttonRound: {
      type: Boolean,
      default: false
    },
    value: [String, Number]
  },
  data() {
    return {
      newValue: null,
      editing: false,
      triggerHidden: true
    }
  },
  computed: {
    style() {
      let style = {}
      let trigger = this.$refs.trigger.$el
      if (this.position === 'right') {
        style = {
          left: `${trigger.offsetLeft + trigger.clientWidth + 20}px`
        }
      } else if (this.position === 'left') {
        let offset = this.$refs.content.clientWidth + trigger.clientWidth + 20
        style = {
          right: `${offset}px`
        }
      }
      return Object.assign({}, style, this.containerStyle)
    }
  },
  watch: {
    modelFrom: {
      immediate: true,
      handler: function(newValue) {
        this.newValue = this.type === 'advanced' ? stripHtmlFromInput(newValue) : newValue
      }
    },
    editing(val) {
      if (val && this.type === 'advanced') {
        setTimeout(() => {
          if (!this.$refs.advancedEditor) return
          placeCaretAtEnd(this.$refs.advancedEditor.caret.el)
        }, 1)
      } else if (val && this.value && this.type === 'text') {
        this.newValue = this.value
        setTimeout(() => {
          this.$refs['text-input'].focus()
        }, 1)
      } else if (val && this.type === 'select') {
        this.newValue = this.value
      } else if (val && this.value && this.type === 'date') {
        this.newValue = moment(this.value).format('YYYY/MM/DD')
      }
    }
  },
  created() {
    if (this.modelFrom && this.type === 'advanced') this.newValue = stripHtmlFromInput(this.modelFrom)
    if (this.type === 'date') this.newValue = moment(this.newValue).format('YYYY/MM/DD')
  },
  methods: {
    save() {
      this.$emit('update', this.newValue)
      this.toggleEditing()
    },
    toggleEditing() {
      this.editing = !this.editing
    },
    showTrigger() {
      this.triggerHidden = false
    },
    hideTrigger() {
      this.triggerHidden = true
    },
    strip(html) {
      this.newValue = stripHtmlFromInput(html)
    },
    datePickerOptions() {
      if (this.dependency && this.maxDate) return [this.dependency, this.maxDate]
      if (this.dependency) return date => date >= this.dependency
      if (this.maxDate) return date => date <= moment(this.maxDate).format('YYYY/MM/DD')
      return () => true
    }
  }
}
</script>

<style lang="stylus" scoped>
>>> .q-date__view.q-date__calendar {
  color black
}

.actions {
  display grid
  grid-auto-flow column
  align-items center
  gap 2px
  margin-left 5px
}
</style>
